import React, { useState, useEffect, useMemo } from 'react';
import { auditService } from '../../services/auditService';
import { AuditLog } from '../../types';
import {
  ShieldCheck,
  Search,
  Filter,
  User,
  RefreshCw,
  X,
  Code,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  BOOKING_CONFIRM: { label: 'اعتماد سداد حجز', color: 'green' },
  BOOKING_CANCEL: { label: 'إلغاء حجز وتحرير مقعد', color: 'red' },
  TRIP_CREATE: { label: 'جدولة رحلة جديدة', color: 'blue' },
  TRIP_UPDATE_STATUS: { label: 'تعديل حالة رحلة', color: 'orange' },
  TRIP_CANCEL: { label: 'إلغاء رحلة', color: 'red' },
  SEAT_LOCK_OVERRIDE: { label: 'تحرير مقعد معلق', color: 'purple' },
  DEMAND_REQUEST_REVIEW: { label: 'مراجعة طلب رحلة', color: 'purple' },
  VEHICLE_ADD: { label: 'إضافة مركبة للأسطول', color: 'blue' },
  UNIVERSITY_ADD: { label: 'إضافة جامعة جديدة', color: 'blue' },
  STUDENT_SUSPEND: { label: 'تجميد حساب طالب', color: 'red' },
  STUDENT_REACTIVATE: { label: 'إعادة تفعيل حساب', color: 'green' },
};

export const AdminAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [resourceFilter, setResourceFilter] = useState('ALL');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(15);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const data = await auditService.getAuditLogs(100);
      setLogs(data);
    } catch (err) {
      console.warn('Error loading audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchAction = actionFilter === 'ALL' || log.action === actionFilter;
      const matchResource = resourceFilter === 'ALL' || log.resourceType === resourceFilter;

      const detailsStr = JSON.stringify(log.details || {});
      const matchSearch =
        searchTerm === '' ||
        log.adminEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.resourceId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        detailsStr.toLowerCase().includes(searchTerm.toLowerCase());

      return matchAction && matchResource && matchSearch;
    });
  }, [logs, actionFilter, resourceFilter, searchTerm]);

  // Sliced logs for table display (15 initial + 10 more on button click)
  const visibleLogs = useMemo(() => {
    return filteredLogs.slice(0, visibleCount);
  }, [filteredLogs, visibleCount]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(15);
  }, [actionFilter, resourceFilter, searchTerm]);

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="admin-badge-purple">سجل الأمان والتدقيق الإداري</span>
            <span className="text-xs text-slate-400">Audit & Compliance Trail</span>
          </div>
          <h2>سجل العمليات والرقابة الإدارية</h2>
          <p className="admin-page-subtitle">
            سجل غير قابل للتعديل يوثق كافة العمليات المالية والإدارية المنفذة في النظام ومن قام بها وتوقيتها بدقة.
          </p>
        </div>

        <div className="admin-page-actions">
          <button
            type="button"
            className="btn-admin-outline"
            onClick={loadLogs}
            disabled={isLoading}
            title="تحديث السجل"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث السجل</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="admin-filter-bar">
        <div className="admin-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="بحث بمعرّف الإداري، رقم الحجز/الرحلة، أو تفاصيل العملية..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear-btn" onClick={() => setSearchTerm('')}>
              ×
            </button>
          )}
        </div>

        <div className="admin-filters-group">
          <div className="filter-select-wrapper">
            <Filter size={15} />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="ALL">جميع أنواع العمليات</option>
              <option value="BOOKING_CONFIRM">اعتماد سداد حجز</option>
              <option value="BOOKING_CANCEL">إلغاء حجز وتحرير مقعد</option>
              <option value="TRIP_CREATE">جدولة رحلة جديدة</option>
              <option value="TRIP_UPDATE_STATUS">تعديل حالة رحلة</option>
              <option value="DEMAND_REQUEST_REVIEW">مراجعة طلب رحلة</option>
              <option value="STUDENT_SUSPEND">تجميد حساب طالب</option>
            </select>
          </div>

          <div className="filter-select-wrapper">
            <select
              value={resourceFilter}
              onChange={(e) => setResourceFilter(e.target.value)}
            >
              <option value="ALL">كافة الموارد</option>
              <option value="BOOKING">حجوزات (BOOKING)</option>
              <option value="TRIP">رحلات (TRIP)</option>
              <option value="DEMAND_REQUEST">طلبات ركاب (DEMAND_REQUEST)</option>
              <option value="STUDENT">طلاب (STUDENT)</option>
              <option value="VEHICLE">مركبات (VEHICLE)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrapper">
        {isLoading ? (
          <div className="admin-table-loading">
            <div className="loading-spinner"></div>
            <span>جارٍ تحميل سجل التدقيق...</span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="admin-table-empty">
            <ShieldCheck size={40} className="empty-icon" />
            <h4>لا توجد سجلات مطابقة للبحث</h4>
            <p>جميع العمليات الإدارية المنفذة ستظهر هنا تلقائياً.</p>
          </div>
        ) : (
          <table className="admin-data-table">
            <thead>
              <tr>
                <th>التوقيت والوقت</th>
                <th>المسؤول (Admin)</th>
                <th>نوع العملية</th>
                <th>المورد المتأثر</th>
                <th>ملخص التغيير</th>
                <th>التفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {visibleLogs.map((log) => {
                const actionMeta = ACTION_LABELS[log.action] || {
                  label: log.action,
                  color: 'slate',
                };
                const formattedDate = new Date(log.timestamp).toLocaleString('ar-EG', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });

                return (
                  <tr key={log.id}>
                    <td>
                      <span className="font-mono text-xs text-slate-700 block whitespace-nowrap">
                        {formattedDate}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <User size={14} className="text-slate-400" />
                        <span className="font-mono text-xs text-slate-800 font-bold">
                          {log.adminEmail}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`kpi-bubble-sm ${actionMeta.color}`}>
                        {actionMeta.label}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono text-xs font-bold text-slate-800 block">
                        {log.resourceId}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {log.resourceType}
                      </span>
                    </td>
                    <td>
                      <div className="max-w-xs text-xs text-slate-600 line-clamp-2">
                        {log.details ? (
                          <span>
                            {Object.entries(log.details)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' • ')}
                          </span>
                        ) : (
                          '—'
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-admin-outline"
                        onClick={() => setSelectedLog(log)}
                        title="عرض كود السجل"
                      >
                        <Code size={13} />
                        <span>فحص</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Load More Footer (15 initial + 10 more on button click) */}
        {filteredLogs.length > 0 && (
          <div style={{ padding: '0 12px 14px' }}>
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredLogs.length)}
              totalCount={filteredLogs.length}
              itemName="سجل رقابي"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredLogs.length)}
            />
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <div className="admin-modal-overlay" onClick={() => setSelectedLog(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-purple-600" />
                <h3>تفاصيل السجل الإداري: {selectedLog.id}</h3>
              </div>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setSelectedLog(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="bg-slate-50 p-4 rounded-xl border space-y-2 mb-4 text-xs">
                <div>
                  <span className="text-slate-500">المسؤول: </span>
                  <strong className="font-mono">{selectedLog.adminEmail}</strong>
                </div>
                <div>
                  <span className="text-slate-500">العملية: </span>
                  <strong className="font-mono text-purple-700">{selectedLog.action}</strong>
                </div>
                <div>
                  <span className="text-slate-500">المورد: </span>
                  <strong className="font-mono">
                    {selectedLog.resourceType} ({selectedLog.resourceId})
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500">التاريخ الكامل: </span>
                  <span className="font-mono">{selectedLog.timestamp}</span>
                </div>
              </div>

              <div className="text-xs font-bold text-slate-700 mb-1">
                البيانات الكاملة (Payload):
              </div>
              <pre
                className="bg-slate-900 text-emerald-400 p-4 rounded-xl font-mono text-xs overflow-x-auto text-left"
                dir="ltr"
              >
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="btn-admin-primary"
                onClick={() => setSelectedLog(null)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
